import { useGeocodingSearch } from "./useGeocodingSearch";
import { config } from "@config";

import { useMemo } from "react";

import type {
  SearchHook,
  SearchHookSuggestResult,
  SearchHookOptions,
  SearchHookSuggestion,
  SearchHookRetrieveResult,
  MapCoords,
  SearchHookSuggestOptions,
} from "../../../types";

import {
  SearchBoxCore,
  type SearchBoxSuggestion,
  SearchSession,
} from "@mapbox/search-js-core";
import { suggestedFeatureToLocation } from "../utils";
import { parseCountryCodes } from "../../../utils";
import { isAbortError } from "../../../is-abort-error";

export function useMapSearch({
  token,
  geocoding,
  proximity,
  types,
}: SearchHookOptions): SearchHook {
  const geocoder = useGeocodingSearch({ token, proximity, types });
  const search = useMemo(() => {
    return new SearchBoxCore({
      accessToken: token,
      proximity,
      types: types ? new Set(types) : undefined,
      // TODO: make country configurable
      country: parseCountryCodes(config.supportedCountries),
    });
  }, [token]);

  const session = useMemo(() => {
    return new SearchSession(search);
  }, [search]);

  async function suggest(
    text: string,
    { countries, bbox }: SearchHookSuggestOptions = {},
  ): Promise<SearchHookSuggestResult<SearchBoxSuggestion>> {
    try {
      const { suggestions, attribution, url } = await session.suggest(text, {
        country:
          countries && countries.length > 0
            ? parseCountryCodes(countries)
            : undefined,
        // [minLng, minLat, maxLng, maxLat]
        bbox: bbox
          ? [bbox.sw.lng, bbox.sw.lat, bbox.ne.lng, bbox.ne.lat]
          : undefined,
      });

      return {
        attribution,
        url,
        suggestions: suggestions.map((suggestion) => ({
          name: suggestion.name,
          formattedLocation: suggestion.place_formatted,
          address: suggestion.full_address,
          language: suggestion.language,
          _raw: suggestion,
        })),
      };
    } catch (error) {
      if (isAbortError(error)) {
        return { attribution: "", url: "", suggestions: [] };
      }
      throw error;
    }
  }

  async function retrieve(
    suggestion: SearchHookSuggestion<SearchBoxSuggestion>,
  ): Promise<SearchHookRetrieveResult> {
    try {
      const { features, attribution, url } = await session.retrieve(
        suggestion._raw,
      );

      return {
        attribution,
        url,
        locations: features.map(suggestedFeatureToLocation),
      };
    } catch (error) {
      if (isAbortError(error)) {
        return { attribution: "", url: "", locations: [] };
      }
      throw error;
    }
  }

  async function fromCoords(coords: MapCoords) {
    try {
      const { features, attribution, url } = await search.reverse(coords);

      return {
        attribution,
        url,
        locations: features.map(suggestedFeatureToLocation),
      };
    } catch (error) {
      if (isAbortError(error)) {
        return { attribution: "", url: "", locations: [] };
      }
      throw error;
    }
  }

  if (geocoding) return geocoder;
  return {
    suggest,
    retrieve,
    fromCoords,
  };
}
